import {
  Camera,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
} from 'three';
import { EventEmitter } from '../core/EventEmitter';

export interface SelectionChange {
  mesh: Mesh;
  clientX: number;
  clientY: number;
  point: Vector3;
  normal: Vector3;
}

interface PointerStart {
  id: number;
  x: number;
  y: number;
}

export class SelectionController {
  readonly changed = new EventEmitter<SelectionChange | null>();
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private readonly highlightMaterial = new MeshBasicMaterial({
    color: 0x31d99b,
    wireframe: true,
    transparent: true,
    opacity: 0.72,
    depthTest: false,
    depthWrite: false,
  });
  private pointerStart: PointerStart | null = null;
  private highlight: Mesh | null = null;
  private selectedMesh: Mesh | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: Camera,
    private readonly modelsRoot: Group,
    private readonly scene: Scene,
    private readonly fitObject: (mesh: Mesh) => void,
    private readonly acceptsPoint: (point: Vector3) => boolean = () => true,
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('keydown', this.onKeyDown);
  }

  focusSelected(): void {
    if (this.selectedMesh) this.fitObject(this.selectedMesh);
  }

  synchronize(): void {
    if (!this.selectedMesh) return;
    let current: Object3D = this.selectedMesh;
    while (current !== this.modelsRoot) {
      if (!current.visible || !current.parent) {
        this.clear();
        return;
      }
      current = current.parent;
    }
  }

  clear(): void {
    this.selectedMesh = null;
    if (this.highlight) {
      this.scene.remove(this.highlight);
      this.highlight = null;
    }
    this.changed.emit(null);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start || start.id !== event.pointerId) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) return;

    const bounds = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster
      .intersectObject(this.modelsRoot, true)
      .find((intersection) => intersection.object instanceof Mesh && this.acceptsPoint(intersection.point));

    if (!hit || !(hit.object instanceof Mesh)) {
      this.clear();
      return;
    }
    const normal = hit.face?.normal.clone().transformDirection(hit.object.matrixWorld)
      ?? new Vector3(0, 1, 0);
    this.select(hit.object, event.clientX, event.clientY, hit.point.clone(), normal);
  };

  private readonly onPointerCancel = (): void => {
    this.pointerStart = null;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.clear();
  };

  private select(
    mesh: Mesh,
    clientX: number,
    clientY: number,
    point: Vector3,
    normal: Vector3,
  ): void {
    if (this.highlight) this.scene.remove(this.highlight);
    mesh.updateWorldMatrix(true, false);
    this.selectedMesh = mesh;
    this.highlight = new Mesh(mesh.geometry, this.highlightMaterial);
    this.highlight.name = 'selection-highlight';
    this.highlight.matrixAutoUpdate = false;
    this.highlight.matrix.copy(mesh.matrixWorld);
    this.highlight.renderOrder = 10_000;
    this.scene.add(this.highlight);
    this.changed.emit({ mesh, clientX, clientY, point, normal });
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.highlight) this.scene.remove(this.highlight);
    this.highlightMaterial.dispose();
  }
}

